/**
 * ChartMind-I2 (inpatient) — collection names, enums, and prompt ids.
 */

export const CHART_MIND_I2_APP_ID = "chartMindI2";
export const CHART_MIND_I2_PATH = "/chartmind-i2";
export const CHART_MIND_I2_ACCESS_COLLECTION = "chartMindI2Access";
export const ADMISSIONS_COLLECTION = "admissionsI2";
export const PLAN_SINGLETON_ID = "current";

export const ADMISSION_STATUS = {
  ACTIVE: "active",
  DISCHARGED: "discharged",
};

export const NOTE_TYPES = {
  HP: "hp",
  DAILY_PROGRESS: "dailyProgress",
  PROCEDURE: "procedure",
  TRANSFER: "transfer",
  CONSULT: "consult",
  DISCHARGE: "discharge",
};

export const NOTE_TYPE_LABELS = {
  [NOTE_TYPES.HP]: "H&P",
  [NOTE_TYPES.DAILY_PROGRESS]: "Daily Progress",
  [NOTE_TYPES.PROCEDURE]: "Procedure",
  [NOTE_TYPES.TRANSFER]: "Transfer",
  [NOTE_TYPES.CONSULT]: "Consult",
  [NOTE_TYPES.DISCHARGE]: "Discharge Summary",
};

export const NOTE_TYPE_ORDER = [
  NOTE_TYPES.HP,
  NOTE_TYPES.DAILY_PROGRESS,
  NOTE_TYPES.PROCEDURE,
  NOTE_TYPES.TRANSFER,
  NOTE_TYPES.CONSULT,
  NOTE_TYPES.DISCHARGE,
];

export const NOTE_STATUS = {
  DRAFT: "draft",
  PENDING_COSIGN: "pendingCosign",
  SIGNED: "signed",
};

export const CODE_STATUS_VALUES = [
  "Full Code",
  "DNR",
  "DNI",
  "DNR/DNI",
  "AND / Comfort Care",
];

/**
 * ChartMind-I2 care-team roles. ChartMind-I still uses the original four
 * tokens (attending, consultant, resident, app). I2 has an eight-token
 * vocabulary: attending / resident / APP / consultingSpecialist /
 * careCoordinator / RN / registrar / medStudent. `consultant` was merged
 * into `consultingSpecialist` (single ad-hoc specialist concept).
 *
 * Roles are membership/display only for now — no note authoring, order
 * entry, or co-sign gates branch on these values.
 *
 * consultingSpecialist is an ad-hoc (non-primary) role. Removal currently
 * does not branch on role — only admitting clinician and pending co-sign
 * are blocked — so CARE_TEAM_AD_HOC_ROLES is documentary until that guard
 * is changed. APP = Advanced Practice Provider (mid-level / NP / PA).
 */
export const CARE_TEAM_ROLES = [
  "attending",
  "resident",
  "app",
  "consultingSpecialist",
  "careCoordinator",
  "rn",
  "registrar",
  "medStudent",
];

export const CARE_TEAM_ROLE_LABELS = {
  attending: "Attending",
  resident: "Resident",
  app: "APP",
  consultingSpecialist: "Consulting Specialist",
  careCoordinator: "Care Coordinator / Case Manager",
  rn: "RN",
  registrar: "Registrar",
  medStudent: "Medical Student",
};

export const CARE_TEAM_AD_HOC_ROLES = ["consultingSpecialist"];

export const MED_SNAPSHOT_TYPES = {
  ADMISSION: "admission",
  IN_HOSPITAL_CHANGE: "inHospitalChange",
  DISCHARGE: "discharge",
};

export const MED_CHANGE_ACTIONS = {
  ADDED: "added",
  STOPPED: "stopped",
  CHANGED: "changed",
};

export const DIAGNOSTIC_ORDER_STATUS = {
  ORDERED: "ordered",
  IN_PROGRESS: "inProgress",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
};

export const DIAGNOSTIC_SOURCE_SYSTEM = {
  MANUAL: "manual",
};

export const RESULT_INPUT_METHODS = {
  DICTATED: "dictated",
  TYPED: "typed",
  SELECTED: "selected",
};

export const RESULT_COMMENT_TAGS = {
  REASSURING: "reassuring",
  NORMAL: "normal",
  ABNORMAL_ACTIONED: "abnormalActioned",
  ABNORMAL_PENDING: "abnormalPending",
};

export const RESULT_COMMENT_TAG_LABELS = {
  [RESULT_COMMENT_TAGS.REASSURING]: "Reassuring",
  [RESULT_COMMENT_TAGS.NORMAL]: "Normal",
  [RESULT_COMMENT_TAGS.ABNORMAL_ACTIONED]: "Abnormal — actioned",
  [RESULT_COMMENT_TAGS.ABNORMAL_PENDING]: "Abnormal — pending",
};

export const PLAN_STATUS = {
  PENDING_REVIEW: "pendingReview",
  REVIEWED: "reviewed",
};

/**
 * Server-written task queue under admissionsI2/{admissionId}/careTasks.
 * Cloud Functions create docs; clients mutate only via chartMindI2MutateCareTask
 * (acknowledge | snooze). `dischargeSummarySection` is wired behind
 * CHARTMIND_I2_DISCHARGE_SECTIONS_ENABLED.
 */
export const CARE_TASKS_COLLECTION = "careTasks";
export const PUSH_SUBSCRIPTIONS_COLLECTION = "chartMindI2PushSubscriptions";
export const NOTE_SECTIONS_COLLECTION = "sections";
export const NOTE_ATTESTATIONS_COLLECTION = "attestations";
export const NURSING_EVENTS_COLLECTION = "events";

/**
 * Draft list for owner review. Not a flowsheet and not a final nursing taxonomy.
 */
export const NURSING_EVENT_TYPES = {
  vitalsAbnormal: "Abnormal vital signs",
  painReassessment: "Pain reassessment",
  prnGiven: "PRN medication given",
  medRefused: "Medication refused / held",
  glucoseAbnormal: "Abnormal glucose",
  fall: "Fall",
  rapidResponse: "Rapid response called",
  changeInMentalStatus: "Change in mental status",
  respiratoryChange: "Oxygen / respiratory change",
  lineOrDrainIssue: "Line, drain, or tube issue",
  intakeOutputConcern: "Intake / output concern",
  skinOrWound: "Skin / wound finding",
  behavioral: "Behavioral event",
  familyCommunication: "Family communication",
  leftUnit: "Patient off unit",
  providerNotified: "Provider notified",
  planChange: "Plan change for nursing",
  newOrderToCarryOut: "New order to carry out",
  familyUpdateGiven: "Family updated",
  other: "Other",
};

export const NURSING_EVENT_SEVERITIES = ["routine", "notable", "urgent"];

export const CARE_TASK_TYPE = {
  DIAGNOSTIC_RESULT_READY: "diagnosticResultReady",
  PENDING_CO_SIGN: "pendingCoSign",
  DISCHARGE_SUMMARY_SECTION: "dischargeSummarySection",
  ATTESTATION_REQUESTED: "attestationRequested",
  STUDENT_DRAFT_READY: "studentDraftReady",
  NURSING_EVENT: "nursingEvent",
  TODO_ASSIGNED: "todoAssigned",
};

/**
 * Draft wording — pending institutional compliance review.
 * Not legal or billing advice.
 */
export const ATTESTATION_PARTICIPATION = {
  withResident: "Saw and examined the patient with the resident",
  independent: "Saw and examined the patient independently",
  discussedOnly: "Discussed with the resident; did not see the patient",
};

/**
 * Draft wording — pending institutional compliance review.
 * Not legal or billing advice.
 */
export const ATTESTATION_TEMPLATES = {
  agreeAsDocumented: {
    text: "I {participationPhrase} on {dateOfService}. I reviewed the resident's note and agree with the findings and plan as documented.",
    requiresAddition: false,
  },
  agreeWithChanges: {
    text: "I {participationPhrase} on {dateOfService}. I reviewed the resident's note and agree, with the following changes or additions:",
    requiresAddition: true,
  },
  discussedOnly: {
    text: "I discussed this patient with the resident on {dateOfService} and reviewed the note.",
    requiresAddition: false,
  },
};

export const CARE_TASK_STATUS = {
  PENDING: "pending",
  ACKNOWLEDGED: "acknowledged",
  COMPLETED: "completed",
  DISMISSED: "dismissed",
};

export const CARE_TASK_MUTATE_ACTIONS = {
  ACKNOWLEDGE: "acknowledge",
  SNOOZE: "snooze",
};

export const SNOOZE_HOURS = 24;

export const DISCHARGE_SECTION_STATUS = {
  NEEDS_INPUT: "needsInput",
  DRAFTED: "drafted",
  READY: "ready",
  REVIEWED: "reviewed",
  /** Server-written AI proposal — human must claim/edit before reviewed. */
  AI_PROPOSED: "aiProposed",
};

export const DISCHARGE_SECTION_TITLES = [
  "Admission diagnosis",
  "Hospital course",
  "Procedures",
  "Results",
  "Medications",
  "Code status",
  "Discharge plan",
  "Follow-up",
];

export const VOICE_CHECKIN_ACTIONS = [
  "openResult",
  "openNote",
  "openAdmission",
  "markTaskAcknowledged",
  "snoozeTask",
];

export const CHART_MIND_I2_VOICE_CHECKIN_PROMPT_ID =
  "chartmind-i2-voice-checkin-intent";

export const CHART_MIND_I2_DISCHARGE_GENERATION_PROMPT_ID =
  "chartmind-i2-discharge-generation";

/**
 * Query Manager / llmGlobalPrompts ids.
 * Category uses existing `chartmind` (not a new QUERY_CATEGORIES key) until
 * the owner confirms a dedicated `chartmind-i2` category. Prompt document
 * ids reuse the ChartMind-I seed so Plan / discharge synthesis still resolve
 * without creating new llmGlobalPrompts docs. I2-only features use
 * chartmind-i2-* (voice check-in, discharge section generation).
 */
export const CHART_MIND_I2_PROMPT_CATEGORY = "chartmind";
export const CHART_MIND_I2_PLAN_PROMPT_ID = "chartmind-i-plan";
export const CHART_MIND_I2_DISCHARGE_PROMPT_ID = "chartmind-i-discharge-summary";

/**
 * Nullable FHIR-oriented external ids (unset until an EHR adapter writes them).
 * Conceptual maps: Encounter / DocumentReference / ServiceRequest /
 * Observation / MedicationStatement.
 */
export const EXTERNAL_ID_FIELDS = {
  ADMISSION_ENCOUNTER: "externalEncounterId",
  NOTE_DOCUMENT: "externalDocumentId",
  ORDER_SERVICE_REQUEST: "externalOrderId",
  RESULT_OBSERVATION: "externalObservationId",
  MED_STATEMENT: "externalStatementId",
};

export const DEFAULT_DIAGNOSTIC_PRESETS = [
  "CBC within expected range",
  "BMP within expected range",
  "Imaging without acute finding",
  "Culture pending",
];
